"use client";

import { X, ArrowRight, Clock, Bell, Shield, TrendingUp, CreditCard, Wallet } from "lucide-react";
import { usePrivy } from "@privy-io/react-auth";
import type { Fight } from "../types";

interface TradePanelProps {
  fight: Fight;
  outcome: 0 | 1;
  onClose: () => void;
  onSwitchOutcome: (outcome: 0 | 1) => void;
}

/* ─── Market not yet open ───────────────────────────────────────────────── */
function MarketComingSoon({ fight, onLogin, authenticated }: { fight: Fight; onLogin: () => void; authenticated: boolean }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-5 py-10 text-center">
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-[5px] bg-[#18181D]">
        <Clock className="h-7 w-7 text-gray-400" />
      </div>

      <h3 className="mb-2 font-[family-name:var(--font-space-grotesk)] text-lg font-bold text-white">
        Mercado no disponible
      </h3>
      <p className="mb-2 text-sm leading-relaxed text-gray-400">
        Las apuestas para <span className="font-medium text-white">{fight.fighter1.alias} vs {fight.fighter2.alias}</span> aún no están abiertas.
      </p>
      <p className="mb-6 text-sm leading-relaxed text-gray-500">
        Se habilitarán próximamente. Crea tu cuenta ahora para apostar en cuanto se abran.
      </p>

      {/* Odds preview */}
      <div className="mb-6 w-full rounded-[5px] border border-[#1A1A1A] bg-[#18181D] p-3">
        <p className="mb-2.5 text-[11px] font-medium text-gray-500">Cuotas estimadas</p>
        <div className="flex gap-2">
          {([0, 1] as const).map((i) => {
            const f = i === 0 ? fight.fighter1 : fight.fighter2;
            return (
              <div key={i} className="flex-1 rounded-[5px] bg-[#0F0F12] px-3 py-2.5 text-center">
                <span className="block text-[10px] text-gray-500">{f.flag} Gana</span>
                <span className="font-[family-name:var(--font-space-grotesk)] text-xs font-bold text-gray-400">
                  {f.alias}
                </span>
                <span className="mt-0.5 block font-[family-name:var(--font-space-grotesk)] text-base font-black tabular-nums text-gray-500">
                  {fight.odds[i]}¢
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {!authenticated && (
        <>
          <button
            onClick={onLogin}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-[5px] bg-[#CCFF00] py-3 font-[family-name:var(--font-space-grotesk)] text-sm font-bold text-[#0A0A0C] transition-[background-color,transform] duration-200 hover:bg-[#d4ff33] active:scale-[0.98]"
          >
            Crear cuenta gratis
            <ArrowRight className="h-4 w-4" />
          </button>

          <p className="mb-3 text-[11px] font-medium text-gray-500">Deposita como quieras</p>
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-3 rounded-[5px] border border-[#1A1A1A] bg-[#18181D] px-3.5 py-2.5">
              <CreditCard className="h-4 w-4 shrink-0 text-gray-400" />
              <div className="text-left">
                <p className="text-xs font-medium text-white">Tarjeta de crédito / débito</p>
                <p className="text-[10px] text-gray-500">Visa, Mastercard, Apple Pay</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-[5px] border border-[#1A1A1A] bg-[#18181D] px-3.5 py-2.5">
              <Wallet className="h-4 w-4 shrink-0 text-gray-400" />
              <div className="text-left">
                <p className="text-xs font-medium text-white">USDC / Crypto</p>
                <p className="text-[10px] text-gray-500">Deposita crypto directamente</p>
              </div>
            </div>
          </div>
        </>
      )}

      {authenticated && (
        <div className="flex w-full items-center gap-3 rounded-[5px] border border-[#1A1A1A] bg-[#18181D] px-4 py-3">
          <Bell className="h-4 w-4 shrink-0 text-[#CCFF00]" />
          <p className="text-left text-xs leading-relaxed text-gray-400">
            Te notificaremos cuando el mercado esté disponible.
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-2 self-stretch">
        <div className="flex items-center gap-2 text-[10px] text-gray-600">
          <Shield className="h-3 w-3 shrink-0" />
          <span>Wallet non-custodial — tú controlas tus fondos</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-gray-600">
          <TrendingUp className="h-3 w-3 shrink-0" />
          <span>Mercados reales en Polymarket</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Main panel ────────────────────────────────────────────────────────── */
export default function TradePanel({ fight, outcome, onClose }: TradePanelProps) {
  const { authenticated, login } = usePrivy();

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#1A1A1A] px-5 py-4">
        <div>
          <p className="text-[10px] font-medium tracking-wider text-gray-500 uppercase">
            {fight.label} · #{fight.number}
          </p>
          <p className="font-[family-name:var(--font-space-grotesk)] text-sm font-bold text-white">
            {fight.fighter1.alias} vs {fight.fighter2.alias}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-[5px] p-1.5 text-gray-500 transition-colors hover:bg-[#18181D] hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <MarketComingSoon fight={fight} onLogin={login} authenticated={authenticated} />
    </div>
  );
}
